import { Button } from "@shared/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { Switch } from "@shared/ui/switch";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (name: string, isPublic: boolean) => Promise<void>;
};

export function CreateBucketDialog({ open, onOpenChange, onConfirm }: Props) {
	const intl = useIntl();
	const [name, setName] = useState("");
	const [isPublic, setIsPublic] = useState(false);
	const [restrictTypes, setRestrictTypes] = useState(false);
	const [allowedMime, setAllowedMime] = useState("");
	const [fileSizeLimit, setFileSizeLimit] = useState("");
	const [sizeUnit, setSizeUnit] = useState("MB");

	const reset = () => {
		setName("");
		setIsPublic(false);
		setRestrictTypes(false);
		setAllowedMime("");
		setFileSizeLimit("");
		setSizeUnit("MB");
	};

	const handleCreate = async () => {
		if (!name.trim()) return;
		await onConfirm(name.trim(), isPublic);
		reset();
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>
						<FormattedMessage
							id="page.storage.createBucket.title"
							defaultMessage="Create a new bucket"
						/>
					</DialogTitle>
					<DialogDescription>
						<FormattedMessage
							id="page.storage.createBucket.description"
							defaultMessage="Buckets store files. Configure access and limits below."
						/>
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-5">
					<div className="space-y-2">
						<Label htmlFor="bucket-name">
							<FormattedMessage
								id="page.storage.createBucket.nameLabel"
								defaultMessage="Name of bucket"
							/>
						</Label>
						<Input
							id="bucket-name"
							placeholder={intl.formatMessage({
								id: "page.storage.createBucket.namePlaceholder",
								defaultMessage: "e.g. avatars",
							})}
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<p className="text-xs text-muted-foreground">
							<FormattedMessage
								id="page.storage.createBucket.nameHint"
								defaultMessage="Bucket names must be lowercase and contain no spaces."
							/>
						</p>
					</div>

					<div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
						<div className="space-y-1">
							<Label htmlFor="public">
								<FormattedMessage
									id="page.storage.createBucket.publicLabel"
									defaultMessage="Public bucket"
								/>
							</Label>
							<p className="text-xs text-muted-foreground">
								<FormattedMessage
									id="page.storage.createBucket.publicHint"
									defaultMessage="Anyone with the URL can view files. RLS still applies to writes."
								/>
							</p>
						</div>
						<Switch id="public" checked={isPublic} onCheckedChange={setIsPublic} />
					</div>

					<div className="space-y-3 rounded-md border border-border p-3">
						<div className="flex items-start justify-between gap-4">
							<div className="space-y-1">
								<Label htmlFor="restrict">
									<FormattedMessage
										id="page.storage.createBucket.restrictLabel"
										defaultMessage="Restrict file types"
									/>
								</Label>
								<p className="text-xs text-muted-foreground">
									<FormattedMessage
										id="page.storage.createBucket.restrictHint"
										defaultMessage="Limit which MIME types can be uploaded."
									/>
								</p>
							</div>
							<Switch id="restrict" checked={restrictTypes} onCheckedChange={setRestrictTypes} />
						</div>
						{restrictTypes && (
							<div className="space-y-2">
								<Label htmlFor="mime">
									<FormattedMessage
										id="page.storage.createBucket.mimeLabel"
										defaultMessage="Allowed MIME types"
									/>
								</Label>
								<Input
									id="mime"
									placeholder={intl.formatMessage({
										id: "page.storage.createBucket.mimePlaceholder",
										defaultMessage: "image/png, image/jpeg, application/pdf",
									})}
									value={allowedMime}
									onChange={(e) => setAllowedMime(e.target.value)}
								/>
								<p className="text-xs text-muted-foreground">
									<FormattedMessage
										id="page.storage.createBucket.mimeHint"
										defaultMessage="Comma-separated. Wildcards like image/* are allowed."
									/>
								</p>
							</div>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="size-limit">
							<FormattedMessage
								id="page.storage.createBucket.sizeLabel"
								defaultMessage="File size limit"
							/>
						</Label>
						<div className="flex gap-2">
							<Input
								id="size-limit"
								type="number"
								min="0"
								placeholder={intl.formatMessage({
									id: "page.storage.createBucket.sizePlaceholder",
									defaultMessage: "e.g. 50",
								})}
								value={fileSizeLimit}
								onChange={(e) => setFileSizeLimit(e.target.value)}
							/>
							<Select
								value={sizeUnit}
								onValueChange={(value) => {
									if (value) setSizeUnit(value);
								}}
							>
								<SelectTrigger className="w-24">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="B">B</SelectItem>
									<SelectItem value="KB">KB</SelectItem>
									<SelectItem value="MB">MB</SelectItem>
									<SelectItem value="GB">GB</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<p className="text-xs text-muted-foreground">
							<FormattedMessage
								id="page.storage.createBucket.sizeHint"
								defaultMessage="Leave empty for no limit."
							/>
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => {
							reset();
							onOpenChange(false);
						}}
					>
						<FormattedMessage id="common.cancel" defaultMessage="Cancel" />
					</Button>
					<Button onClick={handleCreate} disabled={!name.trim()}>
						<FormattedMessage
							id="page.storage.createBucket.submit"
							defaultMessage="Create bucket"
						/>
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
